import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  type Table as TanstackTableType,
  type Column,
  type RowData,
  type TableOptions,
  type ColumnFiltersState,
  type TableState,
  type SortingState,
  type OnChangeFn,
  type FilterFnOption,
  type Row,
  type Cell,
  type Header,
  type ColumnDef,
  type HeaderGroup,
  type ColumnOrderState,
  type VisibilityState,
  type ColumnPinningState,
  type RowSelectionState,
  type ExpandedState,
  type GroupingState,
  type GroupingOptions,
  type ColumnResizeMode
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow
} from "../ui/table";
import ColumnResizer, { type ColumnResizerProps } from "./ColumnResizer";
import ColumnSeparator, { type ColumnSeparatorProps } from "./ColumnSeparator";
import StatusContentSlot, { type ContentSlot } from "./StatusContentSlot";
import DataTableDevTool from "./DataTableDevTool";
import { SuiEmptyDataIcon } from "../icons";
import { Spinner } from "../ui/spinner";
import { cn } from "../../utils";
import { isDefined } from "../../utils/common";
import { stripNullishObject } from "../../utils/filters";
import { getColumnPinningInfo, getColumnPinningStyles, getRowClickHandlers } from "./helpers";

export type DataTableColumnFilterProps<TData extends RowData> = {
  column: Column<TData>;
  table: TanstackTableType<TData>;
};

export type DataTableFilterConfig = {
  enabled: boolean;
  manual?: boolean;
  maxDepth?: number;
  filterFromLeafRows?: boolean;
};

export type DataTableColumnFilter = {
  enabled?: boolean;
  initialState?: ColumnFiltersState;
  state?: ColumnFiltersState;
  onFilterChange?: OnChangeFn<ColumnFiltersState>;
};

export type DataTableGlobalFilter<TData extends RowData> = {
  enabled?: boolean;
  initialState?: any;
  state?: any;
  filterFn?: FilterFnOption<TData>;
  onFilterChange?: OnChangeFn<any>;
  canColumnFilterable?: (column: Column<TData, unknown>) => boolean;
};

export type DataTableFilters<TData extends RowData> = DataTableFilterConfig & {
  column?: DataTableColumnFilter;
  global?: DataTableGlobalFilter<TData>;
};

export type DataTableColumnSorting = {
  enabled: boolean;
  manual?: boolean;
  initialState?: SortingState;
  state?: SortingState;
  sortDescFirst?: boolean;
  onSortingChange?: OnChangeFn<SortingState>;
};

export type DataTableRowSelection<TData> = {
  enabled: boolean;
  initialState?: RowSelectionState;
  state?: RowSelectionState;
  canRowSelectable?: boolean | ((row: Row<TData>) => boolean);
  multiSelect?: boolean;
  onSelectionChange?: OnChangeFn<RowSelectionState>;
};

export type DataTableColumnPinning = {
  enabled: boolean;
  initialState?: ColumnPinningState;
  state?: ColumnPinningState;
  onPinningChange?: OnChangeFn<ColumnPinningState>;
};

export type DataTableColumnOrdering = {
  enabled: boolean;
  initialState?: string[];
  state?: string[];
  onOrderChange?: OnChangeFn<ColumnOrderState>;
};

export type DataTableColumnVisibility = {
  enabled: boolean;
  initialState?: VisibilityState;
  state?: VisibilityState;
  onVisibilityChange?: OnChangeFn<VisibilityState>;
};

export type DataTableRowExpansion<TData extends RowData> = {
  enabled: boolean;
  manual?: boolean;
  initialState?: ExpandedState;
  state?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  isRowExpanded?: (row: Row<TData>) => boolean;
  canRowExpand?: (row: Row<TData>) => boolean;
};

export type DataTableColumnGrouping = {
  manual?: boolean;
  enabled: boolean;
  groupedMode?: false | "reorder" | "remove";
  initialState?: GroupingState;
  state?: GroupingState;
  customAggregationFns?: GroupingOptions["aggregationFns"];
  onGroupingChange?: OnChangeFn<GroupingState>;
};

export type RowClickType = "single" | "double";

export type DataTableRowClickHandler<TData extends RowData> = (
  rowData: TData,
  info: {
    event: React.MouseEvent<HTMLTableRowElement>;
    row: Row<TData>;
    table: TanstackTableType<TData>;
    clickType: RowClickType;
  }
) => void;

export type DataTableRowIdKeyHandler<TData extends RowData> = (
  originalRow: TData,
  index: number,
  parent?: Row<TData>
) => string;

export type DataTableHeaderCell<TData extends RowData> = {
  id: string;
  columnId: string;
  content: React.ReactNode;
  originalCell: Header<TData, unknown>;
};

export type DataTableRenderHeaderProps<TData extends RowData> = {
  headerGroup: HeaderGroup<TData>;
  table: TanstackTableType<TData>;
  cells: DataTableHeaderCell<TData>[];
};

export type DataTableRenderHeaderHandler<TData extends RowData> = (
  props: DataTableRenderHeaderProps<TData>
) => React.ReactNode;

export type DataTableRowCell<TData extends RowData> = {
  id: string;
  columnId: string;
  content: React.ReactNode;
  originalCell: Cell<TData, unknown>;
};

export type DataTableRenderRowProps<TData extends RowData> = {
  row: Row<TData>;
  cells: DataTableRowCell<TData>[];
  table: TanstackTableType<TData>;
};

export type DataTableRenderRowHandler<TData extends RowData> = (
  props: DataTableRenderRowProps<TData>
) => React.ReactNode;

export type DataTableChildrenKeyHandler<TData> = (
  originalRow: TData,
  index: number
) => TData[] | undefined;

export type DatatableColumnResizing = {
  enabled: boolean;
  resizeMode?: ColumnResizeMode;
};

export type DataTableColumnSeparatorProps = {
  headerCell?: ColumnSeparatorProps;
};

export type DataTableComponentProps<TData> = {
  containerProps?: React.ComponentProps<typeof TableContainer>;
  tableProps?: React.ComponentProps<typeof Table>;
  tableHeaderProps?: React.ComponentProps<typeof TableHeader>;
  tableHeadCellProps?:
    | React.ComponentProps<typeof TableHead>
    | ((info: {
        header: Header<TData, unknown>;
        table: TanstackTableType<TData>;
      }) => React.ComponentProps<typeof TableHead>);
  tableBodyProps?: React.ComponentProps<typeof TableBody>;
  tableDataRowProps?:
    | React.ComponentProps<typeof TableRow>
    | ((info: {
        row: Row<TData>;
        table: TanstackTableType<TData>;
      }) => React.ComponentProps<typeof TableRow>);
  tableDataCellProps?:
    | React.ComponentProps<typeof TableCell>
    | ((info: {
        row: Row<TData>;
        cell: Cell<TData, unknown>;
        table: TanstackTableType<TData>;
      }) => React.ComponentProps<typeof TableCell>);
  tableFilterCellProps?:
    | React.ComponentProps<typeof TableCell>
    | ((info: {
        column: Column<TData>;
        table: TanstackTableType<TData>;
      }) => React.ComponentProps<typeof TableCell>);
  columnResizerProps?: Omit<ColumnResizerProps, "header">;
  columnSeparatorProps?: DataTableColumnSeparatorProps;
};

export type DataTableStatusContent = {
  initialLoading?: ContentSlot;
  fetchingMore?: ContentSlot;
  noMoreData?: ContentSlot;
  emptyData?: ContentSlot;
  emptyFilteredData?: ContentSlot;
};

export type StatusContentKey = keyof DataTableStatusContent;

export type ScrollInfo = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  scrollableHeight: number;
  distanceToBottom: number;
  ratioToBottom: number;
  isTopReached: boolean;
  isBottomReached: boolean;
};

export type DataTableScrollFetch = {
  enabled: boolean;
  fetchMore?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  scrollThreshold?: number | ((info: ScrollInfo) => boolean);
};

export type DataTableProps<TData extends RowData> = {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  filters?: DataTableFilters<TData>;
  sorting?: DataTableColumnSorting;
  columnOrder?: DataTableColumnOrdering;
  columnVisibility?: DataTableColumnVisibility;
  columnPinning?: DataTableColumnPinning;
  columnGrouping?: DataTableColumnGrouping;
  columnResizing?: DatatableColumnResizing;
  rowSelection?: DataTableRowSelection<TData>;
  rowExpansion?: DataTableRowExpansion<TData>;
  scrollFetch?: DataTableScrollFetch;
  components?: DataTableComponentProps<TData>;
  activeStatusContent?:
    | (StatusContentKey | null)
    | ((info: { defaultComputedValue: StatusContentKey | null }) => StatusContentKey | null);
  statusContent?: DataTableStatusContent;
  tableRef?: React.RefObject<TanstackTableType<TData> | null>;
  isInitialLoading?: boolean;
  debug?: boolean;
  rowIdKey?: DataTableRowIdKeyHandler<TData>;
  childrenKey?: DataTableChildrenKeyHandler<TData>;
  onRowClick?: DataTableRowClickHandler<TData>;
};

const fallbackData: unknown[] = [];

const DataTable = <TData extends RowData>({
  tableRef,
  isInitialLoading,
  columns,
  data,
  filters,
  sorting,
  columnOrder,
  columnVisibility,
  columnPinning,
  columnGrouping,
  columnResizing,
  rowSelection,
  rowExpansion,
  scrollFetch,
  activeStatusContent,
  statusContent,
  rowIdKey,
  childrenKey,
  onRowClick,
  debug,
  components
}: DataTableProps<TData>) => {
  const table = useReactTable<TData>({
    // required properties
    columns,
    data: data || fallbackData,
    getCoreRowModel: getCoreRowModel(),
    // optional properties
    ...stripNullishObject<TableOptions<TData>>({
      // state for filters, sorting, pinning, row selection, etc.
      initialState: stripNullishObject<TableState>({
        columnFilters:
          filters?.enabled && (filters?.column?.enabled ?? true) ?
            filters?.column?.initialState
          : undefined,
        globalFilter: filters?.enabled ? filters?.global?.initialState : undefined,
        sorting: sorting?.enabled ? sorting?.initialState : undefined,
        columnOrder: columnOrder?.enabled ? columnOrder?.initialState : undefined,
        columnVisibility: columnVisibility?.enabled ? columnVisibility?.initialState : undefined,
        columnPinning: columnPinning?.enabled ? columnPinning?.initialState : undefined,
        rowSelection: rowSelection?.enabled ? rowSelection?.initialState : undefined,
        expanded: rowExpansion?.enabled ? rowExpansion?.initialState : undefined,
        grouping: columnGrouping?.enabled ? columnGrouping?.initialState : undefined
      }),
      state: stripNullishObject<TableState>({
        columnFilters:
          filters?.enabled && (filters?.column?.enabled ?? true) ? filters?.column?.state : undefined,
        globalFilter: filters?.enabled ? filters?.global?.state : undefined,
        sorting: sorting?.enabled ? sorting?.state : undefined,
        columnOrder: columnOrder?.enabled ? columnOrder?.state : undefined,
        columnVisibility: columnVisibility?.enabled ? columnVisibility?.state : undefined,
        columnPinning: columnPinning?.enabled ? columnPinning?.state : undefined,
        rowSelection: rowSelection?.enabled ? rowSelection?.state : undefined,
        expanded: rowExpansion?.enabled ? rowExpansion?.state : undefined,
        grouping: columnGrouping?.enabled ? columnGrouping?.state : undefined
      }),
      // common
      getRowId: rowIdKey,
      getSubRows: childrenKey,
      // shared configs filters column and global
      enableFilters: filters?.enabled ?? false,
      filterFromLeafRows: filters?.enabled && (filters?.filterFromLeafRows ?? false),
      manualFiltering: filters?.enabled && (filters?.manual ?? false),
      maxLeafRowFilterDepth: filters?.enabled ? filters?.maxDepth : undefined,
      // column filters
      getFilteredRowModel: !filters?.manual && filters?.enabled ? getFilteredRowModel() : undefined,
      enableColumnFilters: filters?.enabled && (filters?.column?.enabled ?? true),
      onColumnFiltersChange:
        filters?.enabled && (filters?.column?.enabled ?? true) ?
          filters?.column?.onFilterChange
        : undefined,
      // global filters
      enableGlobalFilter: filters?.enabled && (filters?.global?.enabled ?? true),
      globalFilterFn:
        filters?.enabled && (filters?.global?.enabled ?? true) ? filters?.global?.filterFn : undefined,
      onGlobalFilterChange:
        filters?.enabled && (filters?.global?.enabled ?? true) ?
          filters?.global?.onFilterChange
        : undefined,
      getColumnCanGlobalFilter:
        filters?.enabled && (filters?.global?.enabled ?? true) ?
          (filters?.global?.canColumnFilterable ?? (() => true))
        : undefined,
      // sorting
      getSortedRowModel: !sorting?.manual && sorting?.enabled ? getSortedRowModel() : undefined,
      manualSorting: sorting?.enabled && (sorting?.manual ?? false),
      enableSorting: sorting?.enabled ?? false,
      // default behavior: column with type number is sort by desc and string sort by asc first, we fix force always asc
      sortDescFirst: sorting?.enabled ? (sorting?.sortDescFirst ?? false) : false,
      onSortingChange: sorting?.enabled ? sorting?.onSortingChange : undefined,
      // ordering
      onColumnOrderChange: columnOrder?.enabled ? columnOrder?.onOrderChange : undefined,
      // visibility
      onColumnVisibilityChange:
        columnVisibility?.enabled ? columnVisibility?.onVisibilityChange : undefined,
      // pinning
      enableColumnPinning: columnPinning?.enabled ?? false,
      onColumnPinningChange: columnPinning?.enabled ? columnPinning?.onPinningChange : undefined,
      // row selection
      enableRowSelection: rowSelection?.enabled ? rowSelection?.canRowSelectable : false,
      enableMultiRowSelection: rowSelection?.enabled ? (rowSelection?.multiSelect ?? true) : true,
      onRowSelectionChange: rowSelection?.enabled ? rowSelection?.onSelectionChange : undefined,
      // row expanded
      getExpandedRowModel:
        // when grouping is use then we should open this
        !rowExpansion?.manual && rowExpansion?.enabled ? getExpandedRowModel() : undefined,
      manualExpanding: rowExpansion?.enabled && (rowExpansion?.manual ?? false),
      enableExpanding: rowExpansion?.enabled ?? false,
      getIsRowExpanded: rowExpansion?.enabled ? rowExpansion?.isRowExpanded : undefined,
      getRowCanExpand: rowExpansion?.enabled ? rowExpansion?.canRowExpand : undefined,
      onExpandedChange: rowExpansion?.enabled ? rowExpansion?.onExpandedChange : undefined,
      // grouping
      getGroupedRowModel:
        !columnGrouping?.manual && columnGrouping?.enabled ? getGroupedRowModel() : undefined,
      enableGrouping: columnGrouping?.enabled ?? false,
      manualGrouping: columnGrouping?.enabled && (columnGrouping?.manual ?? false),
      groupedColumnMode: columnGrouping?.enabled ? (columnGrouping?.groupedMode ?? false) : false,
      aggregationFns: columnGrouping?.enabled ? columnGrouping?.customAggregationFns : undefined,
      onGroupingChange: columnGrouping?.enabled ? columnGrouping?.onGroupingChange : undefined,
      // column sizing
      enableColumnResizing: columnResizing?.enabled ?? false,
      columnResizeMode: columnResizing?.enabled ? (columnResizing?.resizeMode ?? "onChange") : "onChange"
    })
  });

  if (isDefined(tableRef) && !isDefined(tableRef?.current)) {
    tableRef!.current = table;
  }

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const isTableEmpty = table.getCoreRowModel().rows.length === 0;
  const isTableEmptyAfterFiltering = table.getRowModel().rows.length === 0;
  const isFiltering = table.getState().columnFilters.length > 0 || !!table.getState().globalFilter;

  const leftVisibleLeftColumns = table.getLeftVisibleLeafColumns();
  const centerVisibleLeafColumns = table.getCenterVisibleLeafColumns();
  const rightVisibleLeafColumns = table.getRightVisibleLeafColumns();

  const { isSomeColumnsFilterable, filterableColumns } = useMemo(() => {
    const mergedColumns: Column<TData, any>[] = [
      ...leftVisibleLeftColumns,
      ...centerVisibleLeafColumns,
      ...rightVisibleLeafColumns
    ];

    const isSomeColumnsFilterable = mergedColumns.some(
      (column) => column.getCanFilter() && column.columnDef.meta?.renderColumnFilter
    );

    return { isSomeColumnsFilterable, filterableColumns: mergedColumns };
  }, [centerVisibleLeafColumns, leftVisibleLeftColumns, rightVisibleLeafColumns]);

  const autoStatusKey = useMemo<StatusContentKey | null>(() => {
    if (isInitialLoading) return "initialLoading";
    if (isTableEmpty) return "emptyData";
    if (isTableEmptyAfterFiltering) return "emptyFilteredData";
    if (scrollFetch?.enabled && !isFiltering && !isInitialLoading) {
      if (scrollFetch.isFetchingMore) return "fetchingMore";
      if (scrollFetch.hasMore === false && !scrollFetch.isFetchingMore) return "noMoreData";
    }
    return null;
  }, [
    isInitialLoading,
    isFiltering,
    isTableEmpty,
    isTableEmptyAfterFiltering,
    scrollFetch?.enabled,
    scrollFetch?.hasMore,
    scrollFetch?.isFetchingMore
  ]);

  const activeStatusContentComputed = useMemo<StatusContentKey | null>(() => {
    if (!activeStatusContent) return autoStatusKey;
    if (typeof activeStatusContent === "function") {
      return activeStatusContent({ defaultComputedValue: autoStatusKey });
    }
    return activeStatusContent;
  }, [activeStatusContent, autoStatusKey]);

  const fetchMoreOnScrollReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (
        !scrollFetch?.enabled ||
        !containerRefElement ||
        scrollFetch?.isFetchingMore ||
        !scrollFetch?.hasMore ||
        !scrollFetch?.fetchMore
      ) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

      const scrollableHeight = scrollHeight - clientHeight;
      const distanceToBottom = scrollableHeight - scrollTop;
      const ratioToBottom = scrollableHeight > 0 ? scrollTop / scrollableHeight : 1;

      const info: ScrollInfo = {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollableHeight,
        distanceToBottom,
        ratioToBottom,
        isTopReached: scrollTop === 0,
        isBottomReached: distanceToBottom <= 0
      };

      let shouldTrigger = false;
      if (typeof scrollFetch.scrollThreshold === "number") {
        shouldTrigger = info.ratioToBottom >= scrollFetch.scrollThreshold;
      } else if (typeof scrollFetch.scrollThreshold === "function") {
        shouldTrigger = scrollFetch.scrollThreshold(info);
      } else {
        shouldTrigger = info.ratioToBottom >= 0.7; // default 70%
      }

      if (shouldTrigger) {
        scrollFetch.fetchMore();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollFetch?.enabled, scrollFetch?.isFetchingMore, scrollFetch?.hasMore, scrollFetch?.fetchMore]
  );

  useEffect(() => {
    fetchMoreOnScrollReached(tableContainerRef.current);
  }, [fetchMoreOnScrollReached]);

  return (
    <TableContainer
      ref={tableContainerRef}
      {...components?.containerProps}
      className={cn(
        "relative w-full overflow-auto h-full max-h-dvh max-w-full bg-white",
        components?.containerProps?.className
      )}
      onScroll={(e) => fetchMoreOnScrollReached(e.currentTarget)}
    >
      {activeStatusContentComputed === "initialLoading" ?
        <StatusContentSlot
          content={statusContent?.initialLoading?.content ?? "Loading..."}
          icon={statusContent?.initialLoading?.icon}
          wrapperProps={statusContent?.initialLoading?.wrapperProps}
          defaultWrapperProps={{
            className: "flex flex-col h-full items-center justify-center text-sm py-4 gap-2",
            ["data-testid" as any]: "status-content-initial-loading"
          }}
          defaultIcon={<Spinner size={48} />}
        />
      : activeStatusContentComputed === "emptyData" ?
        <StatusContentSlot
          content={statusContent?.emptyData?.content ?? "There's nothing here yet."}
          icon={statusContent?.emptyData?.icon}
          wrapperProps={statusContent?.emptyData?.wrapperProps}
          defaultWrapperProps={{
            className: "flex flex-col h-full items-center justify-center text-sm py-4 gap-2",
            ["data-testid" as any]: "status-content-empty-data"
          }}
          defaultIcon={<SuiEmptyDataIcon className="text-[128px]" />}
        />
      : <>
          <Table {...components?.tableProps}>
            {/* header */}
            <TableHeader
              className={cn("sticky top-0 z-10", components?.tableHeaderProps?.className)}
              {...components?.tableHeaderProps}
            >
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const { classes, style } = getColumnPinningStyles(header.column);
                      const useColumnSizing =
                        header.column.columnDef?.meta?.useColumnSizing ??
                        columnResizing?.enabled ??
                        false;

                      const tableHeadCellProps =
                        typeof components?.tableHeadCellProps === "function" ?
                          components?.tableHeadCellProps({ header, table })
                        : components?.tableHeadCellProps;

                      const nextHeader = headerGroup.headers?.[header.index + 1] || header;
                      const { isLastLeftPinnedColumn } = getColumnPinningInfo(header.column);
                      const { isFirstRightPinnedColumn } = getColumnPinningInfo(nextHeader.column);
                      const headerGroupLength = header.headerGroup.headers.length;
                      const showSeparator =
                        header.index !== headerGroupLength - 1 &&
                        !isLastLeftPinnedColumn &&
                        !isFirstRightPinnedColumn;

                      return (
                        <TableHead
                          data-testid={`table-head-${header.id}`}
                          key={header.id}
                          colSpan={header.colSpan}
                          {...tableHeadCellProps}
                          {...header.column.columnDef?.meta?.headerProps}
                          className={cn(
                            classes,
                            tableHeadCellProps?.className,
                            header.column.columnDef?.meta?.headerProps?.className
                          )}
                          style={{
                            ...style,
                            width: useColumnSizing ? header.column.getSize() : undefined,
                            minWidth: useColumnSizing ? header.column.columnDef.minSize : undefined,
                            maxWidth: useColumnSizing ? header.column.columnDef.maxSize : undefined,
                            ...tableHeadCellProps?.style,
                            ...header.column.columnDef?.meta?.headerProps?.style
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                          <ColumnSeparator
                            {...components?.columnSeparatorProps?.headerCell}
                            {...header.column.columnDef?.meta?.columnSeparatorProps}
                            show={
                              header.column.columnDef?.meta?.columnSeparatorProps?.show ??
                              components?.columnSeparatorProps?.headerCell?.show ??
                              showSeparator
                            }
                          />
                          <ColumnResizer header={header} {...components?.columnResizerProps} />
                        </TableHead>
                      );
                    })}
                  </TableRow>
                );
              })}

              {/* filter row */}
              {isSomeColumnsFilterable && (
                <TableRow data-testid="table-filter-row">
                  {filterableColumns.map((column) => {
                    const { classes, style } = getColumnPinningStyles(column);
                    const useColumnSizing =
                      column.columnDef.meta?.useColumnSizing ?? columnResizing?.enabled ?? false;

                    const tableFilterCellProps =
                      typeof components?.tableFilterCellProps === "function" ?
                        components?.tableFilterCellProps({ column, table })
                      : components?.tableFilterCellProps;

                    return (
                      <TableCell
                        data-testid={`table-filter-cell-${column.id}`}
                        key={column.id}
                        {...tableFilterCellProps}
                        {...column.columnDef?.meta?.filterCellProps}
                        className={cn(
                          "bg-white border-b",
                          classes,
                          tableFilterCellProps?.className,
                          column.columnDef?.meta?.filterCellProps?.className
                        )}
                        style={{
                          ...style,
                          width: useColumnSizing ? column.getSize() : undefined,
                          minWidth: useColumnSizing ? column.columnDef.minSize : undefined,
                          maxWidth: useColumnSizing ? column.columnDef.maxSize : undefined,
                          ...tableFilterCellProps?.style,
                          ...column.columnDef?.meta?.filterCellProps?.style
                        }}
                      >
                        {column.getCanFilter() &&
                          column.columnDef.meta?.renderColumnFilter?.({
                            column,
                            table
                          })}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
            </TableHeader>

            {/* data rows */}
            <TableBody {...components?.tableBodyProps}>
              {table.getRowModel().rows.map((row) => {
                const tableDataRowProps =
                  typeof components?.tableDataRowProps === "function" ?
                    components.tableDataRowProps({ row, table }) || {}
                  : components?.tableDataRowProps || {};

                return (
                  <TableRow
                    data-testid={`table-data-row-${row.id}`}
                    {...tableDataRowProps}
                    key={row.id}
                    className={cn("group", tableDataRowProps?.className)}
                    data-state={row.getIsSelected() ? "selected" : "non-selected"}
                    {...getRowClickHandlers(onRowClick ?? (() => {}), {
                      rowData: row.original,
                      row,
                      table
                    })}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const { classes, style } = getColumnPinningStyles(cell.column);
                      const useColumnSizing =
                        cell.column.columnDef.meta?.useColumnSizing ?? columnResizing?.enabled ?? false;

                      const tableDataCellProps =
                        typeof components?.tableDataCellProps === "function" ?
                          components?.tableDataCellProps({ row, cell, table })
                        : components?.tableDataCellProps;

                      return (
                        <TableCell
                          data-testid={`table-data-cell-${cell.id}`}
                          data-row-id={row.id}
                          data-column-id={cell.column.id}
                          key={cell.id}
                          {...tableDataCellProps}
                          {...cell.column.columnDef?.meta?.cellProps}
                          className={cn(
                            {
                              "bg-[#dfeae3]": row.getIsSelected(),
                              "bg-white group-hover:bg-[#eff5f1]": !row.getIsSelected()
                            },
                            classes,
                            tableDataCellProps?.className,
                            cell.column.columnDef?.meta?.cellProps?.className
                          )}
                          style={{
                            ...style,
                            width: useColumnSizing ? cell.column.getSize() : undefined,
                            minWidth: useColumnSizing ? cell.column.columnDef.minSize : undefined,
                            maxWidth: useColumnSizing ? cell.column.columnDef.maxSize : undefined,
                            ...tableDataCellProps?.style,
                            ...cell.column.columnDef?.meta?.cellProps?.style
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {activeStatusContentComputed === "emptyFilteredData" && (
            <StatusContentSlot
              content={
                statusContent?.emptyFilteredData?.content ??
                "No records found. Please try a different search."
              }
              icon={statusContent?.emptyFilteredData?.icon}
              wrapperProps={statusContent?.emptyFilteredData?.wrapperProps}
              defaultWrapperProps={{
                className:
                  "flex flex-col h-[calc(100%-76px)] items-center justify-center text-sm py-4 gap-2",
                ["data-testid" as any]: "status-content-empty-filtered-data"
              }}
              defaultIcon={<SuiEmptyDataIcon className="text-[128px]" />}
            />
          )}

          {activeStatusContentComputed === "fetchingMore" && (
            <StatusContentSlot
              content={statusContent?.fetchingMore?.content ?? "Loading more..."}
              wrapperProps={statusContent?.fetchingMore?.wrapperProps}
              defaultWrapperProps={{
                className: "flex flex-col items-center justify-center text-sm py-4 gap-2",
                ["data-testid" as any]: "status-content-fetching-more"
              }}
            />
          )}

          {activeStatusContentComputed === "noMoreData" && (
            <StatusContentSlot
              content={statusContent?.noMoreData?.content}
              icon={statusContent?.noMoreData?.icon}
              wrapperProps={statusContent?.noMoreData?.wrapperProps}
              defaultWrapperProps={{
                className: "flex flex-col items-center justify-center text-sm py-4 gap-2",
                ["data-testid" as any]: "status-content-no-more-data"
              }}
            />
          )}
        </>
      }

      {/* devtool */}
      {debug && <DataTableDevTool table={table} />}
    </TableContainer>
  );
};

export default DataTable;
